import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useSettings } from "@/hooks/SettingsContext";
import { trackPlanVisitClick } from "@/lib/analytics";

export default function SplashPoster() {
  const { settings } = useSettings();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const triggered = useRef(false);

  useEffect(() => {
    if (triggered.current) return;
    if (!settings) return;
    if (location.pathname.startsWith("/admin")) return;
    if (location.pathname !== "/") return;
    if (settings?.launch_mode === false) return;
    const t = setTimeout(() => {
      triggered.current = true;
      setOpen(true);
    }, 500);
    return () => clearTimeout(t);
  }, [settings, location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [open]);

  const close = () => {
    setOpen(false);
  };

  const planVisit = () => {
    trackPlanVisitClick();
    close();
    setTimeout(() => document.querySelector('[data-testid="service1-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[90] flex items-center justify-center bg-navy/95 p-4 backdrop-blur-md"
          role="dialog"
          aria-modal="true"
          aria-label="Victory Chapel grand opening announcement"
          data-testid="splash-poster"
          onClick={close}
        >
          <button
            type="button"
            onClick={close}
            aria-label="Close and enter site"
            data-testid="splash-close"
            className="absolute right-4 top-4 flex h-12 w-12 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white transition-colors hover:bg-white/20 sm:right-6 sm:top-6"
          >
            <X size={22} />
          </button>
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="flex max-h-full w-full max-w-3xl flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src="/launch-banner.jpg"
              alt="Victory Chapel Clay - Know, Grow, Go. A new church launching January 2027 in Clay County, Florida. A place for real people, real community, a real God."
              className="w-full rounded-2xl shadow-lift"
              width="1942"
              height="722"
            />
            <div className="mt-5 flex w-full flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={planVisit}
                data-testid="splash-plan-visit"
                className="flex-1 rounded-full bg-victory px-7 py-3.5 font-display text-sm font-700 tracking-wide text-white transition-colors hover:bg-victory-dark"
              >
                PLAN YOUR VISIT
              </button>
              <button
                type="button"
                onClick={close}
                data-testid="splash-enter-site"
                className="flex-1 rounded-full border border-white/30 px-7 py-3.5 font-display text-sm font-700 tracking-wide text-white transition-colors hover:bg-white/10"
              >
                ENTER SITE
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
